import React, { BaseSyntheticEvent, useState, useEffect } from 'react';
import { Combobox, comboboxFilterAndLimit, IconSettings } from '@salesforce/design-system-react';

export interface IOption {
    id: string;
    label: string;
    value: string;
    subTitle?: string;
    type?: string;
    [key: string]: any;
}

export interface ISubHeading {
    id: string;
    label: string;
    type?: string;
}

interface ILabel {
    label: string;
    placeholder?: string
}

interface IFilter {
    inputValue: string;
    limit?: number;
    options: IOption[],
    selection: IOption[]
}

type VariantType = 'readonly' | 'inline-listbox';

export type ActionType = 'item-cleared' | 'text-changed' | 'item-selected';

interface IProps {
    options: IOption[];
    onActionPerformed: (actionType: ActionType, value: IOption[] | string) => void;
    limit?: number;
    label: ILabel;
    config: {
        isDisabled?: boolean;
        variation: VariantType;
        isLoading?: boolean;
    }
    value?: string | undefined;
    key?: string
}

interface IMapRawOption {
    label: string;
    value: string;
    subTitle?: string;
    type?: string;
}

/**
 * generates combobox options from raw option
 * @param rawOptions raw options which is needs to be processed
 * @param mapRawOption mapper that maps the relationship between raw options and checkbox option
 * @param labelCb cb to generate label
 * @returns { IOption[] } combobox options
 */
export const generateComboboxOptions = (rawOptions: any[], mapRawOption: IMapRawOption, labelCb?: (option: any) => string): IOption[] => {
    const { label, value, subTitle, type } = mapRawOption;
    return rawOptions.map((option) => ({
        ...option,
        id: option[value],
        value: option[value],
        label: typeof labelCb === 'function' ? labelCb(option) : option[label],
        subTitle: subTitle ? option[subTitle] : undefined,
        type: type ? option[type] : undefined
    }));
}

export const CQCombobox = (props: IProps) => {
    const [inputValue, setInputValue] = useState<string | undefined>('');
    const [selection, setSelection] = useState<IOption[]>([]);

    // runs when the options or value is changed in the props
    useEffect(() => {
        const filteredSelection = props.options.filter(option => option.value === props.value || '');
        if(filteredSelection.length === 0){
            setInputValue(props.value);
        }else{
            setSelection(filteredSelection);
        }
    }, [props.value, props.options]);

    const onChange = (event: BaseSyntheticEvent, { value }: { value: string }) => {
        setInputValue(value);
        props.onActionPerformed('text-changed', value);
    }

    const onSelect = (event: BaseSyntheticEvent, data: {selection: IOption[]}) => {
        setInputValue('');
        setSelection(data.selection);
        props.onActionPerformed('item-selected', data.selection);
    }

    const onRequestRemoveSelectedOption = (event: BaseSyntheticEvent, data: {selection: IOption[]}) => {
        setSelection(data.selection);
        props.onActionPerformed('item-cleared', data.selection);
    }

    const getOptionBasedOnVariant = (variant: VariantType) => {
        if(variant === 'readonly') {
            // add please select option if not present in 0th index
            if(props.options[0] && props.options[0].value !== '') {
                return [
                    {
                        id: '0',
                        label: 'Please Select Option',
                        value: ''
                    }, ...props.options
                ];
            }
            return props.options;
        } 
        
        return comboboxFilterAndLimit({
            inputValue: inputValue,
            options: props.options,
            selection: selection,
            limit: props.limit || 10
        })
    }

    return (
        <IconSettings iconPath="/assets/icons">
            <Combobox
                key={props.key}
                events={{
                    onChange,
                    onSelect,
                    onRequestRemoveSelectedOption
                }}
                labels={props.label}
                options={getOptionBasedOnVariant(props.config.variation)}
                singleInputDisabled={props.config?.isDisabled || false}
                selection={selection}
                value={inputValue}
                variant={props.config.variation}
                menuPosition='relative'
                menuItemVisibleLength= {5}
                hasMenuSpinner={props.config.isLoading || false}
            />
        </IconSettings>
    )
}